// File: src/pages/CareerInsightsPage.jsx
// Description: Career Insights page providing AI-powered career analysis and growth recommendations

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  Psychology,
  School,
  Work,
  Timeline,
  Assessment,
  AutoAwesome,
  ExpandMore,
  Add,
  Edit,
  Save,
  Cancel,
  Lightbulb,
  Flag,
  Business,
  Person,
  SmartToy,
  Download,
  PictureAsPdf
} from '@mui/icons-material';
import { useAI } from '../context/AIContext';
import { useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import AIService from '../services/AIService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { createAuthenticatedAPI } from '../utils/apiClient';

const CareerInsightsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    careerInsights,
    careerGoals,
    updateCareerGoals,
    addCareerInsight
  } = useAI();

  const [loading, setLoading] = useState(false);
  const [editingGoals, setEditingGoals] = useState(false);
  const [tempGoals, setTempGoals] = useState(careerGoals);
  const [refreshing, setRefreshing] = useState(false);
  const [bioText, setBioText] = useState('');
  const contentRef = useRef(null);

  // Create authenticated API client
  const api = createAuthenticatedAPI({ user, logout, navigate });

  // Initialize bio with user's actual bio (same pattern as Resume Enhancer)
  React.useEffect(() => {
    if (user && bioText === '') {
      setBioText(user?.bio || '');
    }
  }, [user, bioText]);

  // Auto-fetch removed: Analysis runs only on user action (button click)

  const handleSaveGoals = async () => {
    setLoading(true);
    try {
      await AIService.simulateAIProcessing(800);
      await updateCareerGoals(tempGoals);
      setEditingGoals(false);
      setTempGoals(careerGoals);
    } catch (error) {
      console.error('Failed to save career goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingGoals(false);
    setTempGoals(careerGoals);
  };

  const handleDownloadReport = async () => {
    if (!contentRef.current) return;
    
    try {
      const node = contentRef.current;
      const canvas = await html2canvas(node, { 
        scale: 2, 
        useCORS: true, 
        backgroundColor: '#ffffff',
        height: node.scrollHeight,
        width: node.scrollWidth
      });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add TouchConnect header
      pdf.setFontSize(20);
      pdf.setTextColor(30, 64, 175); // Primary blue color
      pdf.text('TouchConnect Career Analysis Report', 40, 40);
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated for: ${user?.name || 'Professional'}`, 40, 60);
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 40, 75);
      
      // Add company branding
      pdf.setFontSize(10);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Powered by TouchConnect - Digital Business Card Platform', 40, pageHeight - 30);
      
      // Add content with margins for header/footer
      const imgWidth = pageWidth - 80; // 40px margins on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let position = 90; // Start after header
      let heightLeft = imgHeight;
      const availableHeight = pageHeight - 130; // Account for header and footer

      pdf.addImage(imgData, 'PNG', 40, position, imgWidth, imgHeight);
      heightLeft -= availableHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to new page
        pdf.setFontSize(12);
        pdf.setTextColor(30, 64, 175);
        pdf.text('TouchConnect Career Analysis Report (continued)', 40, 30);
        
        position = 50 - (imgHeight - heightLeft);
        pdf.addImage(imgData, 'PNG', 40, position, imgWidth, imgHeight);
        heightLeft -= availableHeight;
        
        // Add footer to new page
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Powered by TouchConnect - Digital Business Card Platform', 40, pageHeight - 30);
      }

      pdf.save(`TouchConnect_Career_Analysis_${user?.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleAnalyzeInsights = async () => {
    if (!user || !bioText.trim()) return;
    
    setRefreshing(true);
    try {
      // Create user profile for AI analysis using the editable bio
      const userProfile = {
        name: user?.name || 'Professional',
        jobTitle: user?.jobTitle || 'Not specified',
        industry: user?.industry || 'General',
        experience: user?.experience || 'Various',
        location: user?.location || 'Global',
        skills: user?.skills || ['Networking', 'Communication'],
        bio: bioText.trim(), // Use the editable bio text
        education: user?.education || 'Not specified',
        currentCompany: user?.currentCompany || 'Not specified',
        careerGoals: careerGoals || ['Career advancement', 'Skill development'],
        targetRoles: ['AI Product Manager', 'AI Strategy Director', 'AI Consultant']
      };

      const insights = await AIService.getCareerInsights(userProfile);
      
      // Clear existing insights and add new ones
      if (insights && typeof insights === 'object') {
        
        // The insights are already in the correct format from AIService
        // We need to add them directly to the context
        if (insights.trajectory) {
          insights.trajectory.forEach(item => addCareerInsight('trajectory', item));
        }
        if (insights.skillGaps) {
          insights.skillGaps.forEach(item => addCareerInsight('skillGaps', item));
        }
        if (insights.industryTrends) {
          insights.industryTrends.forEach(item => addCareerInsight('industryTrends', item));
        }
        if (insights.nextSteps) {
          insights.nextSteps.forEach(item => addCareerInsight('nextSteps', item));
        }
        if (insights.marketOpportunities) {
          insights.marketOpportunities.forEach(item => addCareerInsight('marketOpportunities', item));
        }
        if (insights.networkingStrategy) {
          addCareerInsight('networkingStrategy', insights.networkingStrategy);
        }
        if (insights.learningPath) {
          addCareerInsight('learningPath', insights.learningPath);
        }
        if (insights.careerGoalsAssessment) {
          addCareerInsight('careerGoalsAssessment', insights.careerGoalsAssessment);
        }
      }
      
    } catch (error) {
      console.error('Failed to refresh career insights:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getSkillPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getSkillGapWidth = (gap) => {
    switch (gap) {
      case 'high': return 80;
      case 'medium': return 50;
      case 'low': return 20;
      default: return 0;
    }
  };

  const addTargetItem = (type) => {
    setTempGoals(prev => ({
      ...prev,
      [type]: [...(prev[type] || []), '']
    }));
  };

  const updateTargetItem = (type, index, value) => {
    setTempGoals(prev => ({
      ...prev,
      [type]: prev[type].map((item, i) => i === index ? value : item)
    }));
  };

  const removeTargetItem = (type, index) => {
    setTempGoals(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };



  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <TrendingUp sx={{ fontSize: 40, color: theme.palette.success.main }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Career Insights
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', mb: 3 }}>
          AI-powered analysis of your career trajectory, skill gaps, and personalized growth recommendations
        </Typography>
      </Box>

      {/* Bio Input Section */}
      <Card sx={{ mb: 4, maxWidth: '900px', mx: 'auto' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Your Professional Profile
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review and update your professional background below. This information will be used to generate personalized career insights.
          </Typography>
          
          <TextField
            label="Professional Bio"
            placeholder="Paste your professional bio or describe your background, experience, skills, and career objectives..."
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            multiline
            rows={6}
            fullWidth
            variant="outlined"
            helperText={`${bioText.length} characters`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {/* Analyze Button */}
            <Button
              variant="contained"
              startIcon={<AutoAwesome />}
              onClick={handleAnalyzeInsights}
              disabled={refreshing || !user || !bioText.trim()}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(30, 64, 175, 0.4)'
                },
                '&:disabled': {
                  transform: 'none',
                  boxShadow: 'none'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {refreshing ? 'Analyzing...' : 'Analyse Career Insights'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Quick tip */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Alert severity="info" sx={{ maxWidth: '600px', mx: 'auto', borderRadius: 2 }}>
          <Typography variant="body2">
            💡 <strong>Tip:</strong> Include your education, work experience, skills, achievements, and career goals for the most accurate analysis.
          </Typography>
        </Alert>
      </Box>

      {/* Download Report Button - Show only when insights are available */}
      {(careerInsights.trajectory.length > 0 || 
        careerInsights.skillGaps.length > 0 || 
        careerInsights.nextSteps.length > 0 || 
        careerInsights.industryTrends.length > 0) && (
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={handleDownloadReport}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: theme.palette.success.main,
              color: theme.palette.success.main,
              '&:hover': {
                borderColor: theme.palette.success.dark,
                backgroundColor: theme.palette.success.light + '10',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.2)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Download Career Report
          </Button>
        </Box>
      )}



      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 4 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            AI is analyzing your career...
          </Typography>
        </Box>
      )}

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid item xs={12} lg={8}>
          <Box ref={contentRef}>
          {/* Career Trajectory Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Timeline sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Career Trajectory
                </Typography>
              </Box>
              
              {careerInsights.trajectory.length > 0 ? (
                <Stack spacing={2}>
                  {careerInsights.trajectory.map((step, index) => (
                    <Paper key={index} sx={{ 
                      p: 2, 
                      border: `1px solid ${theme.palette.divider}`,
                      position: 'relative'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: '50%', 
                          bgcolor: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {step.year}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {typeof step.role === 'string' ? step.role : 'Unknown Role'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {typeof step.company === 'string' ? step.company : 'Unknown Company'}
                          </Typography>
                          {step.confidence && (
                            <Chip 
                              label={`Confidence: ${step.confidence}`} 
                              size="small" 
                              sx={{ 
                                mt: 1,
                                bgcolor: step.confidence === 'high' ? theme.palette.success.main : 
                                         step.confidence === 'medium' ? theme.palette.warning.main : 
                                         theme.palette.error.main,
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      
                      {step.reasoning && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                          {step.reasoning}
                        </Typography>
                      )}
                      
                      {/* Connection Line */}
                      {index < careerInsights.trajectory.length - 1 && (
                        <Box sx={{ 
                          position: 'absolute',
                          left: 20,
                          top: 40,
                          width: 2,
                          height: 20,
                          bgcolor: theme.palette.divider
                        }} />
                      )}
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Work sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Career trajectory analysis will appear here
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Skill Development Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Assessment sx={{ color: theme.palette.warning.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Skill Development Analysis
                </Typography>
              </Box>
              
              {careerInsights.skillGaps.length > 0 ? (
                <Grid container spacing={3}>
                  {careerInsights.skillGaps.map((skill, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {typeof skill.name === 'string' ? skill.name : 'Unknown Skill'}
                          </Typography>
                          <Chip
                            label={typeof skill.priority === 'string' ? skill.priority : 'Unknown'}
                            size="small"
                            sx={{ 
                              bgcolor: getSkillPriorityColor(skill.priority),
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Skill Gap Level
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={getSkillGapWidth(skill.gap)}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                        
                        {skill.currentLevel && skill.targetLevel && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Current: {skill.currentLevel} → Target: {skill.targetLevel}
                          </Typography>
                        )}
                        
                        {skill.estimatedTime && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Estimated time: {skill.estimatedTime}
                          </Typography>
                        )}
                        
                        {skill.recommendedResources && skill.recommendedResources.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Recommended Resources:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {skill.recommendedResources.map((resource, idx) => (
                                <Chip 
                                  key={idx} 
                                  label={resource} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.6rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary">
                          {skill.gap === 'high' ? 'High priority - Focus on developing this skill' :
                           skill.gap === 'medium' ? 'Medium priority - Consider improving this skill' :
                           'Low priority - You have a good foundation'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <School sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Skill analysis will appear here
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Next Steps Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Flag sx={{ color: theme.palette.success.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  AI-Recommended Next Steps
                </Typography>
              </Box>
              
              {careerInsights.nextSteps.length > 0 ? (
                <List>
                  {careerInsights.nextSteps.map((step, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <Box sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          bgcolor: theme.palette.success.main
                        }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {typeof step.action === 'string' ? step.action : 
                               typeof step === 'string' ? step : 'Unknown Action'}
                            </Typography>
                            {step.priority && (
                              <Chip 
                                label={`Priority: ${step.priority}`} 
                                size="small" 
                                sx={{ 
                                  mr: 1,
                                  bgcolor: step.priority === 'high' ? theme.palette.error.main : 
                                           step.priority === 'medium' ? theme.palette.warning.main : 
                                           theme.palette.success.main,
                                  color: 'white',
                                  fontSize: '0.6rem'
                                }}
                              />
                            )}
                            {step.timeline && (
                              <Chip 
                                label={`Timeline: ${step.timeline}`} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.6rem' }}
                              />
                            )}
                            {step.expectedOutcome && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Expected Outcome: {step.expectedOutcome}
                              </Typography>
                            )}
                            {step.resources && step.resources.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Resources:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {step.resources.map((resource, idx) => (
                                    <Chip 
                                      key={idx} 
                                      label={resource} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{ fontSize: '0.5rem' }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'body1', sx: { lineHeight: 1.6 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Lightbulb sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Next steps recommendations will appear here
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Industry Trends Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Business sx={{ color: theme.palette.info.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Industry Trends & Insights
                </Typography>
              </Box>
              
              {careerInsights.industryTrends.length > 0 ? (
                <Stack spacing={2}>
                  {careerInsights.industryTrends.map((trend, index) => (
                    <Paper key={index} sx={{ 
                      p: 2, 
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.default
                    }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          {typeof trend.trend === 'string' ? trend.trend : 
                           typeof trend === 'string' ? trend : 'Unknown Trend'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {trend.impact && (
                            <Chip 
                              label={`Impact: ${trend.impact}`} 
                              size="small" 
                              sx={{ 
                                bgcolor: trend.impact === 'positive' ? theme.palette.success.main : 
                                         trend.impact === 'negative' ? theme.palette.error.main : 
                                         theme.palette.warning.main,
                                color: 'white',
                                fontSize: '0.6rem'
                              }}
                            />
                          )}
                          {trend.relevance && (
                            <Chip 
                              label={`Relevance: ${trend.relevance}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.6rem' }}
                            />
                          )}
                          {trend.timeline && (
                            <Chip 
                              label={`Timeline: ${trend.timeline}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                        
                        {trend.action && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Action:</strong> {trend.action}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TrendingUp sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Industry trends will appear here
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Market Opportunities Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUp sx={{ color: theme.palette.success.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Market Opportunities
                </Typography>
              </Box>
              
              {careerInsights.marketOpportunities && careerInsights.marketOpportunities.length > 0 ? (
                <Stack spacing={2}>
                  {careerInsights.marketOpportunities.map((opportunity, index) => (
                    <Paper key={index} sx={{ 
                      p: 2, 
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.default
                    }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          {opportunity.opportunity || 'Unknown Opportunity'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {opportunity.industry && (
                            <Chip 
                              label={opportunity.industry} 
                              size="small" 
                              sx={{ 
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                fontSize: '0.6rem'
                              }}
                            />
                          )}
                          {opportunity.location && (
                            <Chip 
                              label={opportunity.location} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.6rem' }}
                            />
                          )}
                          {opportunity.timeline && (
                            <Chip 
                              label={`Timeline: ${opportunity.timeline}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.6rem' }}
                            />
                          )}
                          {opportunity.competition && (
                            <Chip 
                              label={`Competition: ${opportunity.competition}`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                        
                        {opportunity.requirements && opportunity.requirements.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              <strong>Requirements:</strong>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {opportunity.requirements.map((req, idx) => (
                                <Chip 
                                  key={idx} 
                                  label={req} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.5rem' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {opportunity.potentialReward && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Potential Reward:</strong> {opportunity.potentialReward}
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TrendingUp sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Market opportunities will appear here
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Career Goals Assessment Card */}
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Assessment sx={{ color: theme.palette.info.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Career Goals Assessment
                </Typography>
              </Box>
              
              {careerInsights.careerGoalsAssessment && (
                Object.keys(careerInsights.careerGoalsAssessment).length > 0 ? (
                  <Grid container spacing={3}>
                    {/* Realistic Goals */}
                    {careerInsights.careerGoalsAssessment.realisticGoals && careerInsights.careerGoalsAssessment.realisticGoals.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.success.main}`, bgcolor: theme.palette.success.light + '10' }}>
                          <Typography variant="h6" sx={{ color: theme.palette.success.main, mb: 2 }}>
                            🎯 Realistic Goals (1-2 years)
                          </Typography>
                          <List dense>
                            {careerInsights.careerGoalsAssessment.realisticGoals.map((goal, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: theme.palette.success.main
                                  }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={goal}
                                  primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.4 } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    )}

                    {/* Challenging Goals */}
                    {careerInsights.careerGoalsAssessment.challengingGoals && careerInsights.careerGoalsAssessment.challengingGoals.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.warning.main}`, bgcolor: theme.palette.warning.light + '10' }}>
                          <Typography variant="h6" sx={{ color: theme.palette.warning.main, mb: 2 }}>
                            🚀 Challenging Goals (2+ years)
                          </Typography>
                          <List dense>
                            {careerInsights.careerGoalsAssessment.challengingGoals.map((goal, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: theme.palette.warning.main
                                  }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={goal}
                                  primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.4 } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    )}

                    {/* Prerequisites */}
                    {careerInsights.careerGoalsAssessment.prerequisites && careerInsights.careerGoalsAssessment.prerequisites.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, bgcolor: theme.palette.primary.light + '10' }}>
                          <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                            📋 Prerequisites
                          </Typography>
                          <List dense>
                            {careerInsights.careerGoalsAssessment.prerequisites.map((prereq, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: theme.palette.primary.main
                                  }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={prereq}
                                  primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.4 } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    )}

                    {/* Risk Factors */}
                    {careerInsights.careerGoalsAssessment.riskFactors && careerInsights.careerGoalsAssessment.riskFactors.length > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.error.main}`, bgcolor: theme.palette.error.light + '10' }}>
                          <Typography variant="h6" sx={{ color: theme.palette.error.main, mb: 2 }}>
                            ⚠️ Risk Factors
                          </Typography>
                          <List dense>
                            {careerInsights.careerGoalsAssessment.riskFactors.map((risk, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                <ListItemIcon>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: theme.palette.error.main
                                  }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={risk}
                                  primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.4 } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Grid>
                    )}

                    {/* Alternative Paths */}
                    {careerInsights.careerGoalsAssessment.alternativePaths && careerInsights.careerGoalsAssessment.alternativePaths.length > 0 && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.secondary.main}`, bgcolor: theme.palette.secondary.light + '10' }}>
                          <Typography variant="h6" sx={{ color: theme.palette.secondary.main, mb: 2 }}>
                            🔄 Alternative Paths
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {careerInsights.careerGoalsAssessment.alternativePaths.map((path, index) => (
                              <Chip 
                                key={index} 
                                label={path} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  borderColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.main
                                }}
                              />
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Assessment sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Career goals assessment will appear here
                    </Typography>
                  </Box>
                )
              )}
            </CardContent>
          </Card>
          </Box>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Career Goals Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Flag sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Career Goals
                  </Typography>
                </Box>
                {!editingGoals ? (
                  <IconButton size="small" onClick={() => setEditingGoals(true)}>
                    <Edit />
                  </IconButton>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={handleSaveGoals} disabled={loading}>
                      <Save />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEdit}>
                      <Cancel />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {editingGoals ? (
                <Stack spacing={2}>
                  <TextField
                    label="Short-term Goal (3-6 months)"
                    value={tempGoals.shortTerm}
                    onChange={(e) => setTempGoals(prev => ({ ...prev, shortTerm: e.target.value }))}
                    multiline
                    rows={2}
                    size="small"
                  />
                  <TextField
                    label="Medium-term Goal (6-12 months)"
                    value={tempGoals.mediumTerm}
                    onChange={(e) => setTempGoals(prev => ({ ...prev, mediumTerm: e.target.value }))}
                    multiline
                    rows={2}
                    size="small"
                  />
                  <TextField
                    label="Long-term Goal (1-3 years)"
                    value={tempGoals.longTerm}
                    onChange={(e) => setTempGoals(prev => ({ ...prev, longTerm: e.target.value }))}
                    multiline
                    rows={2}
                    size="small"
                  />
                  
                  <Divider />
                  
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Target Industries
                    </Typography>
                    {tempGoals.targetIndustries?.map((industry, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          value={industry}
                          onChange={(e) => updateTargetItem('targetIndustries', index, e.target.value)}
                          size="small"
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={() => removeTargetItem('targetIndustries', index)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <Cancel />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => addTargetItem('targetIndustries')}
                      sx={{ mt: 1 }}
                    >
                      Add Industry
                    </Button>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Target Roles
                    </Typography>
                    {tempGoals.targetRoles?.map((role, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          value={role}
                          onChange={(e) => updateTargetItem('targetRoles', index, e.target.value)}
                          size="small"
                          sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={() => removeTargetItem('targetRoles', index)}
                          sx={{ color: theme.palette.error.main }}
                        >
                          <Cancel />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => addTargetItem('targetRoles')}
                      sx={{ mt: 1 }}
                    >
                      Add Role
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {careerGoals.shortTerm && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Short-term (3-6 months)
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {careerGoals.shortTerm}
                      </Typography>
                    </Box>
                  )}
                  
                  {careerGoals.mediumTerm && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                        Medium-term (6-12 months)
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {careerGoals.mediumTerm}
                      </Typography>
                    </Box>
                  )}
                  
                  {careerGoals.longTerm && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                        Long-term (1-3 years)
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {careerGoals.longTerm}
                      </Typography>
                    </Box>
                  )}

                  {(careerGoals.targetIndustries?.length > 0 || careerGoals.targetRoles?.length > 0) && (
                    <>
                      <Divider />
                      {careerGoals.targetIndustries?.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            Target Industries
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {careerGoals.targetIndustries.map((industry, index) => (
                              <Chip key={index} label={industry} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      {careerGoals.targetRoles?.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            Target Roles
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {careerGoals.targetRoles.map((role, index) => (
                              <Chip key={index} label={role} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}

                  {!careerGoals.shortTerm && !careerGoals.mediumTerm && !careerGoals.longTerm && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Flag sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Set your career goals to get personalized insights
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* AI Insights Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutoAwesome sx={{ color: theme.palette.secondary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  AI Insights Summary
                </Typography>
              </Box>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Career Steps</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    {careerInsights.trajectory.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Skills Analyzed</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                    {careerInsights.skillGaps.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Next Steps</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                    {careerInsights.nextSteps.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Industry Trends</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                    {careerInsights.industryTrends.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Market Opportunities</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                    {careerInsights.marketOpportunities ? careerInsights.marketOpportunities.length : 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Career Goals Assessment</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                    {careerInsights.careerGoalsAssessment && Object.keys(careerInsights.careerGoalsAssessment).length > 0 ? 'Available' : 'None'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Actions
              </Typography>
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  href="/profile/edit"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Update Profile
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SmartToy />}
                  href="/ai-assistant"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  AI Networking Assistant
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Business />}
                  href="/saved-profiles"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  View Network
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CareerInsightsPage;
